<script setup>
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, ref, onMounted, watch } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import TeamsAPI from 'dashboard/api/teams';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();
const { accountId } = useAccount();

const currentUser = useMapGetter('getCurrentUser');
const userPerms = computed(() =>
  getUserPermissions(currentUser.value, accountId.value)
);
const isCoordinator = computed(() =>
  userPerms.value.includes('team_coordinator_manage')
);
const isSupervisor = computed(() =>
  userPerms.value.includes('team_supervisor_manage')
);

const loading = ref({});
const searchQuery = ref('');

const teamsList = useMapGetter('teams/getTeams');

// Para cada team cargamos sus miembros y guardamos:
//   - SET de teams donde el usuario actual es coordinator
//   - SET de teams donde el usuario actual es supervisor
//   - mapa { teamId -> { coordinator: User|null, supervisor: User|null } }
//     para poder mostrar el nombre del Coord/Resp asignados en cada fila.
const coordinatedTeamIds = ref(new Set());
const supervisedTeamIds = ref(new Set());
const teamLeads = ref({}); // { [teamId]: { coordinator, supervisor } }

const loadTeamMembership = async () => {
  try {
    const myId = currentUser.value?.id;
    const allTeams = teamsList.value || [];
    const results = await Promise.all(
      allTeams.map(async team => {
        try {
          const { data } = await TeamsAPI.getAgents({ teamId: team.id });
          const me = data.find(m => m.id === myId);
          const coordinator = data.find(m => m.team_role === 'coordinator');
          const supervisor = data.find(m => m.team_role === 'supervisor');
          return {
            teamId: team.id,
            myRole: me?.team_role || null,
            coordinator: coordinator || null,
            supervisor: supervisor || null,
            memberCount: data.length,
          };
        } catch {
          return {
            teamId: team.id,
            myRole: null,
            coordinator: null,
            supervisor: null,
            memberCount: null,
          };
        }
      })
    );
    coordinatedTeamIds.value = new Set(
      results.filter(r => r.myRole === 'coordinator').map(r => r.teamId)
    );
    supervisedTeamIds.value = new Set(
      results.filter(r => r.myRole === 'supervisor').map(r => r.teamId)
    );
    const leadMap = {};
    results.forEach(r => {
      leadMap[r.teamId] = {
        coordinator: r.coordinator,
        supervisor: r.supervisor,
        memberCount: r.memberCount,
      };
    });
    teamLeads.value = leadMap;
    // eslint-disable-next-line no-console
    console.log('[Teams/Index] team leads cargados:', leadMap);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Teams/Index] error cargando team membership:', error);
  }
};

const getCoordinatorName = team => teamLeads.value[team.id]?.coordinator?.name;
const getSupervisorName = team => teamLeads.value[team.id]?.supervisor?.name;
const getMemberCount = team => teamLeads.value[team.id]?.memberCount;

onMounted(() => {
  if ((teamsList.value || []).length) loadTeamMembership();
});

// teamsList llega de forma asíncrona desde el store. Recarga la membresía
// cuando aparezcan o cambien los teams.
watch(
  () => teamsList.value?.map(team => team.id).join(','),
  ids => {
    if (ids) loadTeamMembership();
  }
);

const visibleTeamsList = computed(() => {
  if (isAdmin.value) return teamsList.value;
  if (isCoordinator.value || isSupervisor.value) {
    return teamsList.value.filter(
      t2 =>
        coordinatedTeamIds.value.has(t2.id) ||
        supervisedTeamIds.value.has(t2.id)
    );
  }
  return teamsList.value;
});

const filteredTeamsList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return visibleTeamsList.value;
  return picoSearch(visibleTeamsList.value, query, ['name', 'description']);
});

// Coord puede editar todo del team (incluido nombre/descripción).
// Responsable puede entrar al editor del team SOLO para gestionar agentes
// (el backend rechaza el PATCH al endpoint /teams/:id si solo es supervisor).
const canEditTeam = team => {
  if (isAdmin.value) return true;
  if (isCoordinator.value && coordinatedTeamIds.value.has(team.id)) return true;
  if (isSupervisor.value && supervisedTeamIds.value.has(team.id)) return true;
  return false;
};

// Para el Responsable, el botón "Editar" lleva directamente al paso de agentes
// (no puede editar nombre/descripción del team).
const editRouteFor = team => {
  const isOnlySupervisor =
    !isAdmin.value &&
    !(isCoordinator.value && coordinatedTeamIds.value.has(team.id));
  if (isOnlySupervisor) {
    return {
      name: 'settings_teams_edit_members',
      params: { teamId: team.id },
    };
  }
  return {
    name: 'settings_teams_edit',
    params: { teamId: team.id },
  };
};

const uiFlags = computed(() => getters['teams/getUIFlags'].value);

const deleteTeam = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('teams/delete', id);
    useAlert(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const showDeletePopup = ref(false);
const selectedTeam = ref({});

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeam.value = team;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeam.value = {};
};

const confirmDeletion = () => {
  deleteTeam(selectedTeam.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedTeam.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedTeam.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedTeam.value.name,
  })
);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('TEAMS_SETTINGS.LOADING')"
    :no-records-found="!teamsList.length"
    :no-records-message="$t('TEAMS_SETTINGS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('TEAMS_SETTINGS.HEADER')"
        :description="$t('TEAMS_SETTINGS.DESCRIPTION')"
        :link-text="$t('TEAMS_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('TEAMS_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="team_management"
      >
        <template v-if="teamsList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('TEAMS_SETTINGS.COUNT', { n: teamsList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_teams_new' }">
            <Button :label="$t('TEAMS_SETTINGS.NEW_TEAM')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredTeamsList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('TEAMS_SETTINGS.NO_RESULTS') }}
      </span>

      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="team in filteredTeamsList"
          :key="team.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-start gap-4 min-w-0">
            <div
              class="flex items-center flex-shrink-0 size-10 justify-center rounded-xl outline outline-1 outline-n-weak -outline-offset-1"
            >
              <Icon
                icon="i-lucide-users-round"
                class="size-4 text-n-slate-11"
              />
            </div>
            <div class="flex flex-col items-start gap-1 min-w-0">
              <div class="flex items-center gap-2 min-w-0">
                <span class="block text-heading-3 text-n-slate-12 capitalize">
                  {{ team.name }}
                </span>
                <span
                  v-if="getMemberCount(team) != null"
                  class="inline-flex items-center gap-1 flex-shrink-0 rounded-full bg-n-alpha-2 px-2 py-0.5 text-body-small text-n-slate-11"
                >
                  <Icon icon="i-lucide-users" class="size-3" />
                  {{
                    $t('TEAMS_SETTINGS.LIST.MEMBER_COUNT', {
                      count: getMemberCount(team),
                    })
                  }}
                </span>
              </div>
              <p
                v-if="team.description"
                class="mb-0 text-n-slate-11 text-body-main"
              >
                {{ team.description }}
              </p>
              <p
                class="mb-0 text-body-small text-n-slate-11 inline-flex items-center gap-1.5"
              >
                <Icon
                  icon="i-lucide-user-cog"
                  class="size-3.5 text-n-slate-10"
                />
                <span class="text-n-slate-10">
                  {{ $t('TEAMS_SETTINGS.LIST.COORDINATOR_LABEL') }}
                </span>
                <span class="text-n-slate-12">
                  {{
                    getCoordinatorName(team) ||
                    $t('TEAMS_SETTINGS.LIST.UNASSIGNED')
                  }}
                </span>
              </p>
            </div>
          </div>
          <div class="flex items-start gap-3 flex-shrink-0">
            <div
              class="flex flex-col items-end gap-0.5 mr-1 text-right max-w-[12rem]"
            >
              <span
                class="text-body-small text-n-slate-10 inline-flex items-center gap-1.5"
              >
                <Icon icon="i-lucide-shield" class="size-3.5" />
                {{ $t('TEAMS_SETTINGS.LIST.SUPERVISOR') }}
              </span>
              <span class="text-body-small text-n-slate-12 truncate">
                {{
                  getSupervisorName(team) ||
                  $t('TEAMS_SETTINGS.LIST.UNASSIGNED')
                }}
              </span>
            </div>
            <router-link v-if="canEditTeam(team)" :to="editRouteFor(team)">
              <Button
                v-tooltip.top="$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>

            <Button
              v-if="isAdmin"
              v-tooltip.top="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              :is-loading="loading[team.id]"
              @click="openDelete(team)"
            />
          </div>
        </div>
      </div>
    </template>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
